import React from "react";
import {
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { tokens } from "../assets/theme";
import messages from "../features/calendar/components/messages";
import { FormattedMessage } from "react-intl";

dayjs.extend(customParseFormat);

const dayLabels = {
    1: <FormattedMessage {...messages.monday} />,
    2: <FormattedMessage {...messages.tuesday} />,
    3: <FormattedMessage {...messages.wednesday} />,
    4: <FormattedMessage {...messages.thursday} />,
    5: <FormattedMessage {...messages.friday} />,
    6: <FormattedMessage {...messages.saturday} />,
    0: <FormattedMessage {...messages.sunday} />,
};

const allDayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mån–Sön

function WeekTemplateEditor({ template, onTemplateChange }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (!template) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage {...messages.selectWeekTemplateToEditShifts} />
            </Typography>
        );
    }

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                "& input::-webkit-calendar-picker-indicator": {
                    filter: "invert(1)",
                },
            }
            : {};

    const updateTemplate = (updater) => {
        const cloned = JSON.parse(JSON.stringify(template));
        updater(cloned);
        onTemplateChange(cloned);
    };

    const normalizeTimeString = (value) => {
        if (!value) return "00:00:00";

        if (value.length === 5) {
            return `${value}:00`;
        }

        return value;
    };

    const toTimeFieldValue = (value) => {
        const normalized = normalizeTimeString(value);
        return normalized.substring(0, 5);
    };

    const calculatePlannedProductionHours = (shifts) => {
        return Number(
            shifts
                .reduce((sum, s) => {
                    const startValue = normalizeTimeString(s.startTime);
                    const endValue = normalizeTimeString(s.endTime);

                    const start = dayjs(startValue, "HH:mm:ss", true);
                    const end = dayjs(endValue, "HH:mm:ss", true);

                    if (!start.isValid() || !end.isValid()) {
                        return sum;
                    }

                    const diff = end.diff(start, "minute", true) / 60;
                    return sum + (isNaN(diff) ? 0 : diff);
                }, 0)
                .toFixed(2)
        );
    };

    const normalizeEndTimeForLastShift = (timeValue, isLastShift) => {
        const normalized = normalizeTimeString(timeValue);

        if (isLastShift && normalized === "00:00:00") {
            return "23:59:00";
        }

        return normalized;
    };

    const handleChangeShift = (dayOfWeek, shiftIndex, field, value) => {
        updateTemplate((t) => {
            t.days = [...(t.days || [])];

            const day = t.days.find((d) => d.dayOfWeek === dayOfWeek);
            if (!day) return;

            const shifts = [...(day.shifts || [])];
            if (!shifts[shiftIndex]) return;

            const updatedShift = { ...shifts[shiftIndex] };
            const isLastShift = shiftIndex === shifts.length - 1;

            if (field === "startTime") {
                updatedShift.startTime = normalizeTimeString(value);
            } else if (field === "endTime") {
                updatedShift.endTime = normalizeEndTimeForLastShift(value, isLastShift);
            } else {
                updatedShift[field] = value;
            }

            shifts[shiftIndex] = updatedShift;

            if (field === "endTime" && shiftIndex < shifts.length - 1) {
                const nextShift = { ...shifts[shiftIndex + 1] };
                nextShift.startTime = updatedShift.endTime;
                shifts[shiftIndex + 1] = nextShift;
            }

            day.shifts = shifts;
            day.plannedProductionHours = calculatePlannedProductionHours(shifts);
        });
    };

    const handleAddShift = (dayOfWeek) => {
        updateTemplate((t) => {
            t.days = [...(t.days || [])];

            let day = t.days.find((d) => d.dayOfWeek === dayOfWeek);
            if (!day) {
                day = {
                    dayOfWeek,
                    plannedProductionHours: 0,
                    shifts: [],
                };
                t.days.push(day);
            }

            const existingShifts = [...(day.shifts || [])];
            const previousShift = existingShifts[existingShifts.length - 1];

            const newShift = {
                startTime: previousShift?.endTime || "06:00:00",
                endTime: previousShift?.endTime || "14:00:00",
                label: "Nytt skift",
            };

            day.shifts = [...existingShifts, newShift];
            day.plannedProductionHours = calculatePlannedProductionHours(day.shifts);
        });
    };

    const handleRemoveShift = (dayOfWeek, shiftIndex) => {
        updateTemplate((t) => {
            t.days = [...(t.days || [])];

            const day = t.days.find((d) => d.dayOfWeek === dayOfWeek);
            if (!day) return;

            const shifts = [...(day.shifts || [])];
            day.shifts = shifts.filter((_, idx) => idx !== shiftIndex);
            day.plannedProductionHours = calculatePlannedProductionHours(day.shifts);
        });
    };

    return (
        <>
            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Veckoskift ({template.name})
            </Typography>

            <Grid container spacing={2}>
                {allDayOrder.map((dow) => {
                    const day =
                        (template.days &&
                            template.days.find((d) => d.dayOfWeek === dow)) || {
                            dayOfWeek: dow,
                            plannedProductionHours: 0,
                            shifts: [],
                        };

                    return (
                        <Grid item xs={12} md={6} lg={4} key={dow}>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    borderRadius: 0,
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? colors.primary[400]
                                            : colors.primary[600],
                                    border: `1px solid ${colors.primary[500]}`,
                                    boxShadow: "none",
                                }}
                                elevation={0}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                >
                                    <Typography variant="subtitle1">
                                        {dayLabels[dow]} ({day.plannedProductionHours} h)
                                    </Typography>
                                    <Tooltip title={<FormattedMessage {...messages.addShift} />}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleAddShift(dow)}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                {day.shifts.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        <FormattedMessage {...messages.noShiftsDefined} />
                                    </Typography>
                                ) : (
                                    day.shifts.map((shift, idx) => (
                                        <Stack
                                            key={idx}
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            mb={1}
                                        >
                                            <TextField
                                                label={<FormattedMessage {...messages.start} />}
                                                type="time"
                                                size="small"
                                                value={toTimeFieldValue(shift.startTime)}
                                                onChange={(e) =>
                                                    handleChangeShift(
                                                        dow,
                                                        idx,
                                                        "startTime",
                                                        e.target.value
                                                    )
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                sx={dateTimeIconSx}
                                            />

                                            <TextField
                                                label={<FormattedMessage {...messages.end} />}
                                                type="time"
                                                size="small"
                                                value={toTimeFieldValue(shift.endTime)}
                                                onChange={(e) =>
                                                    handleChangeShift(
                                                        dow,
                                                        idx,
                                                        "endTime",
                                                        e.target.value
                                                    )
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                sx={dateTimeIconSx}
                                            />

                                            <TextField
                                                label={<FormattedMessage {...messages.nameOfShift} />}
                                                size="small"
                                                value={shift.label || ""}
                                                onChange={(e) =>
                                                    handleChangeShift(
                                                        dow,
                                                        idx,
                                                        "label",
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleRemoveShift(dow, idx)
                                                }
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ))
                                )}
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
}

export default WeekTemplateEditor;